import { useEffect, useMemo, useRef, useState } from "react";
import YouTube, { type YouTubeEvent } from "react-youtube";
import type { YouTubePlayer } from "youtube-player/dist/types";
import { Play, Pause, SkipForward, SkipBack, Shuffle, Music4, ListMusic, ExternalLink, MessageSquare, Send, Trash2, Edit2, Sparkles, Layers } from "lucide-react";
import { usePlayback } from "@/lib/playback";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { ReactionsTicker } from "./ReactionsTicker";
import * as api from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import type { PlaylistComment } from "@/lib/mock-data";
import { useIsMobile } from "@/hooks/use-mobile";

// Note: mobile browsers (especially iOS Safari) can block programmatic autoplay.
// We use a mobile-friendly configuration (mute + playsinline) and provide a one-tap fallback.

interface PlaylistPlayerProps {
  layout?: "default" | "focused";
  hideQueue?: boolean;
  showTicker?: boolean;
  showTrackDetails?: boolean;
  tickerVariant?: "list" | "spotlight";
  className?: string;
  videoHeight?: "auto" | number; // "auto" for aspect ratio, number for fixed height in px
  videoMaxHeight?: number; // Max height in px (used when videoHeight is "auto")
  playlistId?: string; // ID of the playlist (round_id, league_id, etc.)
  playlistType?: 'round' | 'league' | 'season' | 'curated' | 'personal'; // Type of playlist
  submissions?: Array<{ spotify_track_id?: string; spotify_url?: string }>; // Submissions for Spotify link generation
}

export const PlaylistPlayer = ({
  layout = "default",
  hideQueue = false,
  showTicker = true,
  showTrackDetails = true,
  tickerVariant = "list",
  className,
  videoHeight = "auto",
  videoMaxHeight,
  playlistId,
  playlistType = 'round',
  submissions = [],
}: PlaylistPlayerProps) => {
  const {
    state: playbackState,
    currentTrack,
    nextTrack,
    previousTrack,
    setOrderIndex,
    setPlaying,
    toggleShuffle,
    playTrack,
    updatePlaybackMeta,
  } = usePlayback();
  const { state, getSubmissionsByRound } = useStore();
  const { toast } = useToast();
  const playerRef = useRef<YouTubePlayer | null>(null);
  // Track whether the user explicitly paused (vs YouTube transient pauses during transitions)
  const manualPauseRef = useRef(false);
  const lastEndedAtRef = useRef<number>(0);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const autoplayProbeTimeoutRef = useRef<number | null>(null);
  const playerReadyRef = useRef(false);
  const SOUND_UNLOCK_KEY = "mlp_sound_unlocked";
  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const isMobile = useIsMobile();

  // Mobile-only: We previously used YouTube's internal playlist mode to maximize autoplay.
  // Autoplay is now disabled, so prefer per-track loading for more reliable skip/play behavior.
  const useYouTubePlaylistMode = false;

  const defer = (fn: () => void) => {
    if (typeof queueMicrotask === "function") {
      queueMicrotask(fn);
      return;
    }
    window.setTimeout(fn, 0);
  };

  const youTubeOpts = useMemo(() => {
    return {
      width: "100%",
      playerVars: {
        autoplay: 0,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
        mute: isMobile ? 1 : 0,
      },
    } as const;
  }, [isMobile]);

  const orderedVideoIds = useMemo(() => {
    const tracks = playbackState.tracks || [];
    const order = playbackState.order || [];
    if (tracks.length === 0 || order.length === 0) return [];
    return order.map((trackIndex) => tracks[trackIndex]?.youtubeId).filter(Boolean) as string[];
  }, [playbackState.tracks, playbackState.order]);

  const getCurrentVideoId = () => {
    const player = playerRef.current;
    try {
      // youtube-player wrapper supports getVideoData()
      const data = (player as any)?.getVideoData?.();
      return data?.video_id as string | undefined;
    } catch {
      return undefined;
    }
  };

  const getPlayerPlaylistIndex = () => {
    try {
      // @ts-expect-error - YouTube IFrame API method exists
      const idx = playerRef.current?.getPlaylistIndex?.();
      return typeof idx === "number" ? idx : null;
    } catch {
      return null;
    }
  };
  
  // Comments state
  const [comments, setComments] = useState<PlaylistComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  
  // Get fun facts, round status, and submission for current track
  const currentTrackSubmission = useMemo(() => {
    if (!currentTrack) return undefined;
    return getSubmissionsByRound(currentTrack.roundId).find(s => s.id === currentTrack.submissionId);
  }, [currentTrack, getSubmissionsByRound]);
  
  const currentTrackFunFacts = useMemo(() => {
    return currentTrackSubmission?.fun_facts;
  }, [currentTrackSubmission]);
  
  const currentRoundStatus = useMemo(() => {
    if (!currentTrack) return undefined;
    const round = state.rounds.find(r => r.id === currentTrack.roundId);
    return round?.status;
  }, [currentTrack, state.rounds]);
  
  // Display mode for playlist comments section
  type PlaylistDisplayMode = 'comments' | 'funfacts' | 'both';
  const getDefaultPlaylistDisplayMode = (): PlaylistDisplayMode => {
    if (currentRoundStatus === 'submitting' || currentRoundStatus === 'voting') {
      return 'funfacts';
    }
    return 'both';
  };
  
  const [playlistDisplayMode, setPlaylistDisplayMode] = useState<PlaylistDisplayMode>(getDefaultPlaylistDisplayMode());
  
  const cyclePlaylistDisplayMode = () => {
    setPlaylistDisplayMode((prev) => {
      if (prev === 'comments') return 'funfacts';
      if (prev === 'funfacts') return 'both';
      return 'comments';
    });
  };
  
  useEffect(() => {
    setPlaylistDisplayMode(getDefaultPlaylistDisplayMode());
  }, [currentRoundStatus]);

  // Load comments when playlistId changes
  useEffect(() => {
    if (!playlistId || !playlistType) {
      return;
    }

    const loadComments = async () => {
      setLoadingComments(true);
      try {
        const loadedComments = await api.playlistCommentAPI.getComments(playlistId, playlistType);
        setComments(loadedComments);
      } catch (error) {
        console.error('Failed to load comments:', error);
        toast({
          title: 'Error',
          description: 'Failed to load comments',
          variant: 'destructive',
        });
      } finally {
        setLoadingComments(false);
      }
    };

    loadComments();
  }, [playlistId, playlistType, toast]);

  // Handler functions for comments
  const handleSubmitComment = async () => {
    if (!playlistId || !playlistType || !state.currentUser || !newComment.trim()) {
      return;
    }

    try {
      const newCommentData = await api.playlistCommentAPI.createComment({
        playlist_id: playlistId,
        playlist_type: playlistType,
        user_id: state.currentUser.id,
        comment: newComment.trim(),
      });
      
      setComments((prev) => [newCommentData, ...prev]);
      setNewComment('');
      toast({
        title: 'Success',
        description: 'Comment posted',
      });
    } catch (error) {
      console.error('Failed to submit comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to post comment',
        variant: 'destructive',
      });
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editCommentText.trim()) {
      return;
    }

    try {
      const updatedComment = await api.playlistCommentAPI.updateComment(commentId, editCommentText.trim());
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? updatedComment : c))
      );
      setEditingCommentId(null);
      setEditCommentText('');
      toast({
        title: 'Success',
        description: 'Comment updated',
      });
    } catch (error) {
      console.error('Failed to update comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to update comment',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await api.playlistCommentAPI.deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast({
        title: 'Success',
        description: 'Comment deleted',
      });
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete comment',
        variant: 'destructive',
      });
    }
  };
  
  const getPlaylistDisplayModeIcon = () => {
    switch (playlistDisplayMode) {
      case 'comments':
        return <MessageSquare className="h-4 w-4" />;
      case 'funfacts':
        return <Sparkles className="h-4 w-4" />;
      case 'both':
        return <Layers className="h-4 w-4" />;
    }
  };
  
  const shouldShowPlaylistComments = (playlistDisplayMode === 'comments' || playlistDisplayMode === 'both') && currentRoundStatus === 'completed';
  const shouldShowPlaylistFunFacts = (playlistDisplayMode === 'funfacts' || playlistDisplayMode === 'both') && currentTrackFunFacts && currentTrackFunFacts.length > 0;

  const getPlayerState = () => {
    if (typeof window === "undefined") {
      return undefined;
    }
    return window.YT?.PlayerState;
  };

  const startPlaylistAtIndex = (index: number) => {
    if (!playerRef.current || orderedVideoIds.length === 0) {
      return;
    }
    try {
      // @ts-expect-error - YouTube IFrame API method exists
      playerRef.current.loadPlaylist(orderedVideoIds, index, 0);
      playerRef.current.playVideo?.();
      setOrderIndex(index, true);
    } catch {
      try {
        // @ts-expect-error - YouTube IFrame API method exists
        playerRef.current.playVideoAt?.(index);
        playerRef.current.playVideo?.();
        setOrderIndex(index, true);
      } catch {
        // no-op
      }
    }
  };

  const syncMuteState = () => {
    try {
      const muted = playerRef.current?.isMuted?.();
      if (typeof muted === "boolean") {
        setIsMuted(muted);
      }
    } catch {
      // no-op
    }
  };

  const markSoundUnlocked = () => {
    setSoundUnlocked(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SOUND_UNLOCK_KEY, "true");
    }
  };

  const tryUnmute = () => {
    try {
      if (playerRef.current?.isMuted?.()) {
        playerRef.current?.unMute?.();
      }
    } catch {
      // no-op
    }
    syncMuteState();
  };

  const currentTrackDetails = useMemo(() => {
    if (!currentTrack) {
      return null;
    }
    return {
      ...currentTrack,
      user: state.users.find((user) => user.username === currentTrack.submitter),
    };
  }, [currentTrack, state.users]);

  // Generate Spotify playlist URL
  const spotifyPlaylistUrl = useMemo(() => {
    if (submissions.length === 0) {
      return null;
    }
    return api.generateSpotifyPlaylistUrl(submissions);
  }, [submissions]);

  const handlePlayerReady = (event: YouTubeEvent<number>) => {
    playerRef.current = event.target;
    playerReadyRef.current = true;
    syncMuteState();

    // If we can, initialize playlist mode on mobile.
    if (useYouTubePlaylistMode && orderedVideoIds.length > 0) {
      try {
        if (playbackState.isPlaying) {
          // Start playlist playback inside the iframe (most reliable for mobile autoplay).
          // @ts-expect-error - YouTube IFrame API method exists
          event.target.loadPlaylist(orderedVideoIds, playbackState.orderIndex, 0);
        } else {
          // @ts-expect-error - YouTube IFrame API method exists
          event.target.cuePlaylist(orderedVideoIds, playbackState.orderIndex, 0);
        }
      } catch {
        // fall back to normal load-by-id flow
      }
    }

    if (soundUnlocked) {
      tryUnmute();
    }

    if (playbackState.isPlaying) {
      event.target.playVideo();
    }
  };

  const handlePlayerStateChange = (event: YouTubeEvent<number>) => {
    const playerState = event.data;
    const stateEnum = getPlayerState();
    if (!stateEnum) {
      return;
    }

    // Some environments (especially mobile) briefly enter UNSTARTED/CUED during track transitions.
    // If playback is supposed to be running, proactively kick playback back on.
    if (
      (playerState === stateEnum.UNSTARTED || playerState === stateEnum.CUED) &&
      playbackState.isPlaying &&
      !manualPauseRef.current
    ) {
      defer(() => {
        try {
          playerRef.current?.playVideo?.();
        } catch {
          // no-op
        }
      });
      setTimeout(() => {
        try {
          playerRef.current?.playVideo?.();
        } catch {
          // no-op
        }
      }, 200);
      // If we don't transition to PLAYING shortly, consider autoplay blocked and show a one-tap resume.
      if (autoplayProbeTimeoutRef.current) {
        window.clearTimeout(autoplayProbeTimeoutRef.current);
      }
      autoplayProbeTimeoutRef.current = window.setTimeout(() => {
        const ps = playerRef.current?.getPlayerState?.();
        if (ps !== stateEnum.PLAYING) {
          setAutoplayBlocked(true);
        }
      }, 800);
      return;
    }

    if (playerState === stateEnum.PLAYING) {
      manualPauseRef.current = false;
      setPlaying(true);
      setAutoplayBlocked(false);
      if (soundUnlocked) {
        tryUnmute();
      }
      syncMuteState();

      // Mobile playlist mode: update our UI index from the player without reloading videos.
      if (useYouTubePlaylistMode) {
        const playlistIdx = getPlayerPlaylistIndex();
        if (playlistIdx != null) {
          setOrderIndex(playlistIdx, true);
        }
      }
    } else if (playerState === stateEnum.PAUSED) {
      // YouTube can emit PAUSED briefly when switching tracks; don't treat that as user intent.
      const now = Date.now();
      const recentlyEnded = now - lastEndedAtRef.current < 1500;
      if (recentlyEnded && playbackState.isPlaying) {
        // Keep playback "on" and let the next track start.
        setPlaying(true);
        return;
      }
      if (manualPauseRef.current) {
        setPlaying(false);
      } else if (playbackState.isPlaying) {
        // If we're supposed to be playing, attempt to resume.
        setPlaying(true);
        defer(() => {
          try {
            playerRef.current?.playVideo?.();
          } catch {
            // no-op
          }
        });
      } else {
        setPlaying(false);
      }
      syncMuteState();
    } else if (playerState === stateEnum.ENDED) {
      lastEndedAtRef.current = Date.now();
      manualPauseRef.current = false;
      // Ensure playback stays enabled while advancing to next track.
      setPlaying(true);
      if (!useYouTubePlaylistMode) {
        nextTrack(true);
      }
    }
  };

  const handlePlayPause = () => {
    if (!playerRef.current) {
      return;
    }
    const stateEnum = getPlayerState();
    if (!stateEnum) {
      return;
    }

    const playerStatus = playerRef.current.getPlayerState?.();
    if (playerStatus === stateEnum.PLAYING) {
      manualPauseRef.current = true;
      playerRef.current.pauseVideo();
      setPlaying(false);
    } else {
      manualPauseRef.current = false;
      markSoundUnlocked();
      tryUnmute();
      if (useYouTubePlaylistMode) {
        try {
          const targetIndex = playbackState.orderIndex;
          // @ts-expect-error - YouTube IFrame API method exists
          if (typeof playerRef.current?.playVideoAt === "function") {
            // @ts-expect-error - YouTube IFrame API method exists
            playerRef.current.playVideoAt(targetIndex);
          } else {
            startPlaylistAtIndex(targetIndex);
          }
        } catch {
          startPlaylistAtIndex(playbackState.orderIndex);
        }
      } else {
        playerRef.current.playVideo();
      }
      setPlaying(true);
    }
  };

  useEffect(() => {
    return () => {
      if (autoplayProbeTimeoutRef.current) {
        window.clearTimeout(autoplayProbeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(SOUND_UNLOCK_KEY);
    if (stored === "true") {
      setSoundUnlocked(true);
    }
  }, []);

  const playlistKey = useMemo(() => orderedVideoIds.join("|"), [orderedVideoIds]);
  const lastPlaylistKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!useYouTubePlaylistMode || !playerRef.current || !playerReadyRef.current) {
      return;
    }
    if (orderedVideoIds.length === 0) return;
    if (playlistKey === lastPlaylistKeyRef.current) return;

    lastPlaylistKeyRef.current = playlistKey;
    try {
      if (playbackState.isPlaying) {
        // @ts-expect-error - YouTube IFrame API method exists
        playerRef.current.loadPlaylist(orderedVideoIds, playbackState.orderIndex, 0);
      } else {
        // @ts-expect-error - YouTube IFrame API method exists
        playerRef.current.cuePlaylist(orderedVideoIds, playbackState.orderIndex, 0);
      }
    } catch {
      // no-op
    }
  }, [useYouTubePlaylistMode, orderedVideoIds, playlistKey, playbackState.isPlaying, playbackState.orderIndex]);

  useEffect(() => {
    if (!playerRef.current || !currentTrack) {
      return;
    }

    const youtubeId = currentTrack.youtubeId;
    if (!youtubeId) {
      return;
    }

    const player = playerRef.current;

    if (useYouTubePlaylistMode) return;

    if (typeof player.loadVideoById === "function") {
      player.loadVideoById(youtubeId);
    }
    if (playbackState.isPlaying && typeof player.playVideo === "function") {
      player.playVideo();
    }
  }, [currentTrack, useYouTubePlaylistMode]); // only react to track changes (non-playlist mode)

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !currentTrack) {
      return;
    }

    if (playbackState.isPlaying) {
      if (typeof player.playVideo === "function") {
        player.playVideo();
      }
      if (soundUnlocked) {
        tryUnmute();
      }
    } else if (typeof player.pauseVideo === "function") {
      player.pauseVideo();
    }
  }, [playbackState.isPlaying, currentTrack, soundUnlocked]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    let frame: number | null = null;

    const updateMeta = () => {
      const current =
        typeof player.getCurrentTime === "function" ? player.getCurrentTime() : 0;
      const duration =
        typeof player.getDuration === "function" ? player.getDuration() : 0;
      updatePlaybackMeta({
        progressSeconds: current,
        durationSeconds: duration,
      });
    };

    const step = () => {
      updateMeta();
      frame = window.requestAnimationFrame(step);
    };

    if (playbackState.isPlaying) {
      step();
    } else {
      updateMeta();
    }

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [playbackState.isPlaying, updatePlaybackMeta]);

  const playlistLabel = playbackState.playlistLabel ?? 'Current Playlist';
  const isFocused = layout === "focused";

  return (
    <Card
      className={cn(
        "shadow-xl shadow-primary/10",
        isFocused ? "border-0 bg-transparent shadow-none" : "",
        className,
      )}
    >
      <CardHeader
        className={cn(
          "flex flex-col gap-4 md:flex-row md:items-center md:justify-between",
          isFocused ? "px-0" : "",
        )}
      >
        <div className="flex-1">
          <CardTitle className={cn("flex items-center gap-2", isFocused ? "text-xl md:text-2xl" : "text-2xl")}>
            <Music4 className="h-6 w-6 text-primary" />
            {playlistLabel}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Playback is {playbackState.isPlaying ? 'playing' : 'paused'} •{' '}
            {playbackState.isShuffled ? 'Shuffle on' : 'Shuffle off'} • Tap play to start
          </p>
        </div>
        {spotifyPlaylistUrl && (
          <Button
            variant="outline"
            size="sm"
            asChild
            className="flex-shrink-0"
          >
            <a href={spotifyPlaylistUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Open in Spotify
            </a>
          </Button>
        )}
        {currentTrackDetails && !isFocused && (
          <Badge variant="outline" className="text-xs">
            Playing {currentTrackDetails.title} by {currentTrackDetails.artist}
          </Badge>
        )}
      </CardHeader>
      <CardContent className={cn("space-y-6", isFocused ? "px-0" : "")}>
        <div
          className={cn(
            "grid gap-6",
            hideQueue ? "" : "lg:grid-cols-[minmax(0,1fr)_320px]",
          )}
        >
          <div className="space-y-4">
            {videoHeight === "auto" ? (
              videoMaxHeight ? (
                <div
                  className={cn(
                    "overflow-hidden rounded-xl border border-primary/20",
                    isFocused ? "shadow-2xl shadow-primary/20" : "",
                  )}
                  style={{
                    width: "100%",
                    aspectRatio: "16/9",
                    maxHeight: `${videoMaxHeight}px`,
                    height: "auto",
                  } as React.CSSProperties & { aspectRatio: string }}
                >
                  {(autoplayBlocked || isMuted) && isMobile && (
                    <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-center p-2">
                      <Button
                        size="sm"
                        className="bg-music-gradient text-white shadow-md"
                        onClick={() => {
                          manualPauseRef.current = false;
                          setAutoplayBlocked(false);
                          markSoundUnlocked();
                          setPlaying(true);
                          try {
                            playerRef.current?.unMute?.();
                          } catch {
                            // no-op
                          }
                          try {
                            playerRef.current?.playVideo?.();
                          } catch {
                            // no-op
                          }
                          syncMuteState();
                        }}
                      >
                        {autoplayBlocked ? "Tap to play" : "Tap for sound"}
                      </Button>
                    </div>
                  )}
                  {currentTrack?.youtubeId ? (
                    <YouTube
                      videoId={currentTrack.youtubeId}
                      opts={youTubeOpts}
                      onReady={handlePlayerReady}
                      onStateChange={handlePlayerStateChange}
                      className="h-full w-full"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-muted">
                      <p className="text-muted-foreground">Select a track to start playback</p>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className={cn(
                    "overflow-hidden rounded-xl border border-primary/20 w-full",
                    isFocused ? "shadow-2xl shadow-primary/20" : "",
                  )}
                >
                  <AspectRatio ratio={16 / 9}>
                    {(autoplayBlocked || isMuted) && isMobile && (
                      <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-center p-2">
                        <Button
                          size="sm"
                          className="bg-music-gradient text-white shadow-md"
                          onClick={() => {
                            manualPauseRef.current = false;
                            setAutoplayBlocked(false);
                            markSoundUnlocked();
                            setPlaying(true);
                            try {
                              playerRef.current?.unMute?.();
                            } catch {
                              // no-op
                            }
                            try {
                              playerRef.current?.playVideo?.();
                            } catch {
                              // no-op
                            }
                            syncMuteState();
                          }}
                        >
                          {autoplayBlocked ? "Tap to play" : "Tap for sound"}
                        </Button>
                      </div>
                    )}
                    {currentTrack?.youtubeId ? (
                      <YouTube
                        videoId={currentTrack.youtubeId}
                        opts={youTubeOpts}
                        onReady={handlePlayerReady}
                        onStateChange={handlePlayerStateChange}
                        className="h-full w-full"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-muted">
                        <p className="text-muted-foreground">Select a track to start playback</p>
                      </div>
                    )}
                  </AspectRatio>
                </div>
              )
            ) : (
              <div
                className={cn(
                  "overflow-hidden rounded-xl border border-primary/20 w-full",
                  isFocused ? "shadow-2xl shadow-primary/20" : "",
                )}
                style={{ height: `${videoHeight}px`, width: "100%" }}
              >
                {(autoplayBlocked || isMuted) && isMobile && (
                  <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-center p-2">
                    <Button
                      size="sm"
                      className="bg-music-gradient text-white shadow-md"
                      onClick={() => {
                        manualPauseRef.current = false;
                        setAutoplayBlocked(false);
                        markSoundUnlocked();
                        setPlaying(true);
                        try {
                          playerRef.current?.unMute?.();
                        } catch {
                          // no-op
                        }
                        try {
                          playerRef.current?.playVideo?.();
                        } catch {
                          // no-op
                        }
                        syncMuteState();
                      }}
                    >
                      {autoplayBlocked ? "Tap to play" : "Tap for sound"}
                    </Button>
                  </div>
                )}
                {currentTrack?.youtubeId ? (
                  <YouTube
                    videoId={currentTrack.youtubeId}
                    opts={youTubeOpts}
                    onReady={handlePlayerReady}
                    onStateChange={handlePlayerStateChange}
                    className="h-full w-full"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-muted">
                    <p className="text-muted-foreground">Select a track to start playback</p>
                  </div>
                )}
              </div>
            )}

            {/* Mobile: show ticker directly under video */}
            {showTicker && (
              <div className="lg:hidden space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Comments
                </h3>
                {currentTrack ? (
                  <ReactionsTicker
                    roundId={currentTrack.roundId}
                    submissionId={currentTrack.submissionId}
                    isActive={playbackState.isPlaying}
                    staticReactions={currentTrack.reactions}
                    variant={tickerVariant}
                    funFacts={currentTrackFunFacts}
                    roundStatus={currentRoundStatus}
                    submitterComment={currentTrackSubmission?.submitter_comment}
                  />
                ) : (
                  <div className="flex h-[240px] items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 p-6 text-center text-sm text-muted-foreground">
                    Start playback to see comments.
                  </div>
                )}
              </div>
            )}

            {/* Mobile: track details directly under ticker */}
            {showTrackDetails && currentTrackDetails && (
              <div className="lg:hidden rounded-lg border border-muted bg-muted/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{currentTrackDetails.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {currentTrackDetails.artist} • Submitted by {currentTrackDetails.submitter}
                    </p>
                  </div>
                  <Badge variant="secondary">
                    Round{" "}
                    {currentTrackDetails.roundNumber
                      ? `#${currentTrackDetails.roundNumber}`
                      : currentTrackDetails.roundId}
                  </Badge>
                </div>
                {currentTrackDetails.comment && (
                  <p className="mt-3 text-sm text-muted-foreground">{currentTrackDetails.comment}</p>
                )}
              </div>
            )}

            {/* Playback Controls - Always visible */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={() => {
                  manualPauseRef.current = false;
                  markSoundUnlocked();
                  tryUnmute();
                  if (useYouTubePlaylistMode) {
                    const idx = getPlayerPlaylistIndex();
                    const nextIdx =
                      idx == null
                        ? Math.max(0, playbackState.orderIndex - 1)
                        : (idx - 1 + orderedVideoIds.length) % orderedVideoIds.length;
                    try {
                      // Prefer native method if available
                      // @ts-expect-error - YouTube IFrame API method exists
                      if (typeof playerRef.current?.previousVideo === "function") {
                        // @ts-expect-error - YouTube IFrame API method exists
                        playerRef.current.previousVideo();
                        playerRef.current?.playVideo?.();
                      } else {
                        startPlaylistAtIndex(nextIdx);
                      }
                    } catch {
                      startPlaylistAtIndex(nextIdx);
                    }
                    // Sync UI index after the player updates.
                    window.setTimeout(() => {
                      const updated = getPlayerPlaylistIndex();
                      if (updated != null) setOrderIndex(updated, true);
                    }, 150);
                  } else {
                    previousTrack(true);
                  }
                }}
                disabled={playbackState.tracks.length === 0}
                title="Previous track"
              >
                <SkipBack className="h-5 w-5" />
              </Button>
              <Button
                className="h-12 min-w-[120px] rounded-full px-6"
                onClick={handlePlayPause}
                disabled={!currentTrack}
                title={playbackState.isPlaying ? "Pause" : "Play"}
              >
                {playbackState.isPlaying ? (
                  <Pause className="mr-2 h-5 w-5" />
                ) : (
                  <Play className="mr-2 h-5 w-5" />
                )}
                {playbackState.isPlaying ? "Pause" : "Play"}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={() => {
                  manualPauseRef.current = false;
                  markSoundUnlocked();
                  tryUnmute();
                  if (useYouTubePlaylistMode) {
                    const idx = getPlayerPlaylistIndex();
                    const nextIdx =
                      idx == null
                        ? Math.min(orderedVideoIds.length - 1, playbackState.orderIndex + 1)
                        : (idx + 1) % orderedVideoIds.length;
                    try {
                      // Prefer native method if available
                      // @ts-expect-error - YouTube IFrame API method exists
                      if (typeof playerRef.current?.nextVideo === "function") {
                        // @ts-expect-error - YouTube IFrame API method exists
                        playerRef.current.nextVideo();
                        playerRef.current?.playVideo?.();
                      } else {
                        startPlaylistAtIndex(nextIdx);
                      }
                    } catch {
                      startPlaylistAtIndex(nextIdx);
                    }
                    window.setTimeout(() => {
                      const updated = getPlayerPlaylistIndex();
                      if (updated != null) setOrderIndex(updated, true);
                    }, 150);
                  } else {
                    nextTrack(true);
                  }
                }}
                disabled={playbackState.tracks.length === 0}
                title="Next track"
              >
                <SkipForward className="h-5 w-5" />
              </Button>
              <Button
                variant={playbackState.isShuffled ? "default" : "outline"}
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={toggleShuffle}
                disabled={playbackState.tracks.length <= 1}
                title={playbackState.isShuffled ? "Shuffle on" : "Shuffle off"}
              >
                <Shuffle className="h-5 w-5" />
              </Button>
            </div>

            <div
              className={cn(
                "grid gap-3",
                !hideQueue && showTrackDetails && currentTrackDetails ? "lg:grid-cols-2" : "lg:grid-cols-1",
              )}
            >
              {showTrackDetails && currentTrackDetails && (
                <div className="hidden lg:block rounded-lg border border-muted bg-muted/30 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{currentTrackDetails.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {currentTrackDetails.artist} • Submitted by {currentTrackDetails.submitter}
                      </p>
                    </div>
                    <Badge variant="secondary">
                      Round{" "}
                      {currentTrackDetails.roundNumber
                        ? `#${currentTrackDetails.roundNumber}`
                        : currentTrackDetails.roundId}
                    </Badge>
                  </div>
                  {currentTrackDetails.comment && (
                    <p className="mt-3 text-sm text-muted-foreground">{currentTrackDetails.comment}</p>
                  )}
                </div>
              )}

              {!hideQueue && (
                <div className="flex flex-col gap-2">
                  <div className="mb-1 flex items-center gap-2 text-muted-foreground">
                    <ListMusic className="h-4 w-4" />
                    <span className="text-sm font-medium uppercase tracking-wider">Playlist Queue</span>
                  </div>
                  <ScrollArea className="h-[180px] rounded-lg border border-muted bg-muted/40 p-2">
                    <div className="space-y-2">
                      {playbackState.tracks.map((track, index) => {
                        const isActive = playbackState.order[playbackState.orderIndex] === index;
                        return (
                          <button
                            key={track.submissionId}
                            type="button"
                            onClick={() => {
                              if (useYouTubePlaylistMode) {
                                const playlistIdx = orderedVideoIds.findIndex((id) => id === track.youtubeId);
                                if (playlistIdx >= 0) {
                                  try {
                                    // @ts-expect-error - YouTube IFrame API method exists
                                    playerRef.current?.playVideoAt?.(playlistIdx);
                                    playerRef.current?.playVideo?.();
                                  } catch {
                                    // no-op
                                  }
                                  setOrderIndex(playlistIdx, true);
                                  return;
                                }
                              }
                              playTrack(track.submissionId);
                            }}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors",
                              isActive
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-transparent bg-background hover:border-primary/30 hover:bg-muted",
                            )}
                          >
                            <div className="flex h-9 w-16 flex-shrink-0 items-center justify-center rounded bg-muted text-xs font-semibold text-muted-foreground">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground">{track.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {track.artist} • {track.submitter}
                              </p>
                            </div>
                            {track.youtubeId && (
                              <Badge variant="outline" className="text-[10px]">
                                YouTube
                              </Badge>
                            )}
                          </button>
                        );
                      })}
                      {playbackState.tracks.length === 0 && (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                          No tracks available yet. Submit songs to start the playlist.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </div>

          {showTicker && (
            <div className={cn("hidden lg:block space-y-3", hideQueue ? "lg:col-span-2" : "")}>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Live Votes &amp; Comments
              </h3>
              {currentTrack ? (
                <ReactionsTicker
                  roundId={currentTrack.roundId}
                  submissionId={currentTrack.submissionId}
                  isActive={playbackState.isPlaying}
                  staticReactions={currentTrack.reactions}
                  variant={tickerVariant}
                  funFacts={currentTrackFunFacts}
                  roundStatus={currentRoundStatus}
                  submitterComment={currentTrackSubmission?.submitter_comment}
                />
              ) : (
                <div className="flex h-[320px] items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 p-6 text-center text-sm text-muted-foreground">
                  Start playback to see votes and comments roll in.
                </div>
              )}
            </div>
          )}

          {/* Playlist Comments Section */}
          {playlistId && (
            <div className={cn("space-y-4", hideQueue ? "lg:col-span-2" : "")}>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {playlistDisplayMode === 'comments' ? 'Playlist Comments' :
                   playlistDisplayMode === 'funfacts' ? 'Fun Facts' :
                   'Playlist Comments & Fun Facts'}
                </h3>
                {currentTrack && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={cyclePlaylistDisplayMode}
                    className="h-7 w-7"
                    title={`Switch to ${playlistDisplayMode === 'comments' ? 'fun facts' : playlistDisplayMode === 'funfacts' ? 'both' : 'comments only'}`}
                  >
                    {getPlaylistDisplayModeIcon()}
                  </Button>
                )}
              </div>
              
              {/* Fun Facts Display for Current Track */}
              {shouldShowPlaylistFunFacts && currentTrackFunFacts && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <h4 className="text-sm font-semibold text-foreground">
                        Fun Facts: {currentTrack?.title} by {currentTrack?.artist}
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {currentTrackFunFacts.map((fact, idx) => (
                        <div key={idx} className="rounded-md border border-primary/10 bg-background p-3">
                          <p className="text-sm text-foreground">{fact}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {!shouldShowPlaylistFunFacts && (currentRoundStatus === 'submitting' || currentRoundStatus === 'voting') && (
                <div className="text-center text-sm text-muted-foreground py-4 rounded-lg border border-dashed border-muted-foreground/30">
                  Fun facts will appear here once available for the current track.
                </div>
              )}
              
              {/* Comment Input */}
              {state.currentUser && (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Add a comment about this playlist..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSubmitComment}
                      disabled={!newComment.trim()}
                      size="sm"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Post Comment
                    </Button>
                  </div>
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-3">
                {loadingComments ? (
                  <div className="text-center text-sm text-muted-foreground py-4">
                    Loading comments...
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-4">
                    No comments yet. Be the first to comment!
                  </div>
                ) : (
                  <ScrollArea className="h-[300px] rounded-lg border border-muted bg-muted/40 p-4">
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="rounded-lg border border-muted bg-background p-3">
                          {editingCommentId === comment.id ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                rows={3}
                                className="resize-none"
                              />
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditCommentText('');
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleEditComment(comment.id)}
                                  disabled={!editCommentText.trim()}
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-sm text-foreground">
                                      {comment.username || 'Unknown User'}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(comment.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-foreground whitespace-pre-wrap">
                                    {comment.comment}
                                  </p>
                                </div>
                                {state.currentUser?.id === comment.user_id && (
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                        setEditingCommentId(comment.id);
                                        setEditCommentText(comment.comment);
                                      }}
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-destructive"
                                      onClick={() => handleDeleteComment(comment.id)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


